import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Document, pdfjs } from 'react-pdf'
import {
  Backdrop,
  Box,
  CircularProgress,
  Fade,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import { isObj, isStr } from 'lib/util'
import { useAttachmentAPI, useStyles } from 'platform/react/hook'
import ApplicationContext from 'platform/react/context/application'
import Icon from 'ui/Element/Icon'
import Toolbar from 'ui/Element/Attachment/Item/Pdf/Toolbar'
import Thumbnails from 'ui/Element/Attachment/Item/Pdf/Thumbnails'
import Pages from 'ui/Element/Attachment/Item/Pdf/Pages'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'dist/pdf.worker.js',
  window.location.origin,
).toString()

/**
 * Styles
 *
 * @param {Object} theme    the theme
 * @param {number} spacing  spacing to apply to the elements
 * @returns {Object}
 */
const styles = (theme, { spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: theme.spacing(spacing),
    backgroundColor: theme.palette.background.content,
    height: '100%',
    width: '100%',
  },
  loader: {
    position: 'absolute',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    paddingLeft: '0.75rem',
    paddingBottom: '0.75rem',
    color: 'rgba(0, 0, 0, 0.8)',
    zIndex: 1000,
    columnGap: theme.spacing(1),
  },
  document: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    gap: '1rem',
    width: '100%',
    '& .react-pdf__message': {
      width: '100%',
    },
  },
  pageContainer: {
    height: '100%',
  },
  noData: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.black.main,
  },
})

/**
 * Returns the current language set in the intl manager.
 *
 * @returns {*|string}
 */
const useCurrentLanguage = () => {
  const { intl } = useContext(ApplicationContext)
  return intl.get()?.language || '*'
}

/**
 * Helper function to show a no data screen in case we couldn't load the PDF.
 *
 * @param {Object} noDataProps            props
 * @param {Object} noDataProps.icon       the icon to show
 * @param {Object} noDataProps.labels     labels to display
 * @param {Object} noDataProps.classes    styles
 * @returns {JSX.Element}
 * @private
 */
const _noData = ({ icon, labels, classes }) => {
  return (
    <Box className={classes.root}>
      <Icon
        source={icon}
        color={'black'}
        size={'5rem'}
      />
      <Typography variant={'16/bold'}>{labels.title}</Typography>
      <Typography variant={'14/medium'}>{labels.text}</Typography>
    </Box>
  )
}

/**
 * Shows {@link Skeleton}s when loading the PDF.
 *
 * @param {Object} loadingProps         props
 * @param {string} loadingProps.width   the width of the thumbnail container
 * @param {Object} loadingProps.classes styles
 * @returns {JSX.Element}
 * @private
 */
const Loading = ({ showThumbnails, width, classes, hidden }) => (hidden ? null : (
  <Fade
    in={true}
    timeout={{
      enter: 1500,
    }}
  >
    <Box className={classes.root}>
      <Skeleton
        width={'100%'}
        height={'6.5rem'}
        variant={'rounded'}
      />
      <Box
        sx={{ display: 'flex' }}
        className={classes.document}
      >
        {showThumbnails && (
          <Skeleton
            width={width}
            height={'100%'}
            variant={'rounded'}
          />
        )}
        <Skeleton
          width={'100%'}
          height={'100%'}
          variant={'rounded'}
        />
      </Box>
    </Box>
  </Fade>
))

/**
 * Mapping between breakpoint keywords (like `xs`) and actual dimensions
 * in `rem`s.
 *
 * @type {{xl: string, md: string, sm: string, xs: string, lg: string}}
 * @private
 */
const _dimensions = {
  xs: '9.5',
  sm: '11',
  md: '13',
  lg: '15',
  xl: '20',
}

/**
 * Simple Pdf Component
 *
 * @param {Object} pdfProps                         props
 * @param {number|string} pdfProps.width            width of the thumbnail bar
 * @param {number} pdfProps.page                    page of PDF to be loaded
 * @param {string} pdfProps.term                    search term to be highlighted
 * @param {...Object} pdfProps.props                additional props
 * @param {string} [pdfProps.props.group]           attachment group
 * @param {string} pdfProps.props.ofLabel           translations of 'of'
 * @param {string} pdfProps.props.currentlyLabel    translations of 'currently'
 * @param {string} pdfProps.props.originalSizeLabel translations of 'Original size'
 * @param {string} pdfProps.props.errorLabel        label when the pdf could not be loaded
 * @param {string} pdfProps.props.errorDescription  description when the pdf could not be loaded
 * @param {string} pdfProps.props.errorIcon         icon when the pdf could not be loaded
 * @param {string} pdfProps.props.events            events passed into this component
 * @param {Function} pdfProps.props.events.onOpen   handler for getting the pdf url
 * @param {Object} ref                              forwarded ref
 * @returns {JSX.Element}
 */
const SimplePdf = ({ attachment, thumbnailWidth = 'xs', page: initialPage, term: initialSearchTerm, ...props }, ref) => {
  const {
    group,
    ofLabel,
    currentlyLabel,
    originalSizeLabel,
    errorLabel,
    closeLabel,
    errorDescription,
    searchLabel,
    loadingLabel,
    errorIcon,
    events,
    spacing = 0,
    showDownloadButton = true,
    searchBannerOnTop = true,
    showThumbnails: showInitialThumbnails = true,
    showFeedback = false,
    showFavorite = false,
  } = props

  const icons = {
    thumbnailIcon: props.thumbnailIcon,
    searchIcon: props.searchIcon,
    minusIcon: props.minusIcon,
    plusIcon: props.plusIcon,
    printIcon: props.printIcon,
    downloadIcon: props.downloadIcon,
    upIcon: props.upIcon,
    downIcon: props.downIcon,
    feedbackIcon: props.feedbackIcon,
    favoriteIcon: props.favoriteIcon,
    noFavoriteIcon: props.noFavoriteIcon,
  }

  const {
    onOpen = null,
    onState = null,
    onAttachment = null,
    onFeedbackOpen = null,
    onFavoriteOpen = null,
    onFavoriteClick = null,
  } = events || {}

  const [item, setItem] = useState(null)
  const [file, setFile] = useState(null)
  const [filename, setFilename] = useState(null)
  const [url, setUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const [numPages, setNumPages] = useState()
  const [pageNumber, setPageNumber] = useState(initialPage || 1)
  const [pageScale, setPageScale] = useState(0.75)
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '')
  const [searchResults, setSearchResults] = useState([])
  const [highlightedResult, setHighlightedResult] = useState(-1)
  const [error, setError] = useState(false)

  // const [loadedPages, setLoadedPages] = useState([])
  const [currentPageLoaded, setCurrentPageLoaded] = useState(false)
  const [lastPageLoaded, setLastPageLoaded] = useState(false)
  const [showThumbnails, setShowThumbnails] = useState(showInitialThumbnails)

  const classes = useStyles(styles, { spacing })()

  const frameRef = useRef(null)
  const wrapperRef = useRef(null)

  const [documentWidth, setDocumentWidth] = useState(null)

  const onResize = useCallback(() => {
    if (wrapperRef?.current) {
      const documentRect = wrapperRef.current.getBoundingClientRect()
      setDocumentWidth(documentRect.width)
    }
  }, [])

  // Recalculating height if we resize the window
  useEffect(() => {
    window.addEventListener('resize', onResize)

    return () => { window.removeEventListener('resize', onResize) }
  }, [])

  useEffect(() => { onResize() }, [wrapperRef?.current])

  const errorComponent = _noData({
    icon: errorIcon,
    labels: {
      title: errorLabel,
      text: errorDescription,
    },
    classes: { root: classes.noData },
  })

  const { state } = useAttachmentAPI({ attachment: item || { value: { name: '' } }, group })
  const language = useCurrentLanguage()
  const options = useMemo(() => ({
    httpHeaders: { 'Accept-Language': language },
    withCredentials: true,
  }), [language])

  // In case we get the attachment info through the attachment prop, we need to
  // set item to it.
  useEffect(() => {
    attachment && isObj(attachment) && setItem(attachment)
  }, [attachment])

  useEffect(() => {
    // Executing onOpen handler
    (async () => {
      const pdfDoc = attachment ? null : await onOpen?.(null)

      if (pdfDoc) {
        // The handler may return a string, in which case we set the url to it
        // if it returns an object, we assume it's the attachment info, and we
        // set item to it.
        isStr(pdfDoc) ? setUrl(pdfDoc) : setItem(pdfDoc)
      }
    })()
  }, [])

  // Once we fetched the attachment, we set the url to its blob url, also we
  // execute onState handler (if available) so that any parent component that
  // needs information about the attachment state gets it.
  useEffect(() => {
    if (state?.src) {
      onState?.(state)
      setUrl(state.src)
    }
  }, [state?.src])

  // Handler for when the pdf has successfully loaded
  const onDocumentLoadSuccess = async (pdf) => {
    const data = await pdf.getData()

    const newFilename = item
      ? item.value.name
      : pdf._transport._fullReader._filename
    const newFile = new File([data], newFilename, { type: 'application/pdf' })

    setFilename(newFilename)
    setFile(URL.createObjectURL(newFile))
    setIsLoading(false)
    setNumPages(pdf.numPages)
    setPageNumber(initialPage || 1)
  }

  // Handler for then we click on a thumbnail
  const onItemClick = useCallback(({ pageNumber: newPage }) => {
    setPageNumber(newPage)
  }, [])

  // handler for changing a page
  const changePage = (offset) => {
    setPageNumber(prevPageNumber => prevPageNumber + offset)
  }

  // Handle clicking on download button
  const handleDownload = useCallback(() => {
    const link = document.createElement('a')

    const newFilename = !onAttachment ? null : onAttachment?.(
      { value: { value: { name: filename } } },
    )
    const downloadName = !newFilename ? filename : newFilename?.value?.name || filename

    link.setAttribute('download', downloadName)
    link.setAttribute('href', file)
    link.click()
  }, [file, filename])

  // Jump to the first result as soon as we typed in a search term
  // Without this, we need to press the down button once to get to
  // the first result, which isn't good if the result is already on
  // the page we are currently viewing
  useEffect(() => {
    if (searchResults.length && highlightedResult === null) {
      setHighlightedResult(searchResults[0])
    }
  }, [searchResults])

  // Jump to the page with the current search result
  useEffect(() => {
    highlightedResult?.pageNumber && setPageNumber(highlightedResult?.pageNumber)
  }, [highlightedResult])

  // If we load this component with `initialSearchTerm` and `initialPage`,
  // we need to directly jump to the first result on {@initialPage}
  useEffect(() => {
    const initialSearchResultPage = searchResults
      .find(searchResult => searchResult?.pageNumber === initialPage)
    // Only do this, if we have not already highlighted a specific search
    // result (highlightedResult !== -1). Meaning, only on initial render.
    if (highlightedResult === -1 && initialSearchResultPage) {
      initialSearchTerm === searchTerm
        && initialPage === pageNumber
        && setHighlightedResult(initialSearchResultPage)
    }
  }, [searchResults, highlightedResult])

  useEffect(() => {
    if (!searchTerm || searchTerm === '') {
      setHighlightedResult(-1)
      setSearchResults([])
    }
  }, [searchTerm])

  return (
    <>
      {file && (
        <iframe
          key={file}
          src={file}
          ref={frameRef}
          style={{ display: 'none' }}
        />
      )}
      <Grid
        container
        ref={wrapperRef}
        className={classes.root}
      >
        {error || isLoading || !filename ? null : (
          <Toolbar
            icons={icons}
            document={item || { value: { name: filename } }}
            pages={numPages}
            filename={filename}
            frameRef={frameRef}
            pageScale={pageScale}
            width={documentWidth}
            searchTerm={searchTerm}
            currentPage={pageNumber}
            showFeedback={showFeedback}
            showFavorite={showFavorite}
            searchResults={searchResults}
            showThumbnails={showThumbnails}
            searchBannerOnTop={searchBannerOnTop}
            showDownloadButton={showDownloadButton}
            highlightedSearchResult={highlightedResult}
            labels={{
              ofLabel,
              originalSizeLabel,
              currentlyLabel,
              searchLabel,
              closeLabel,
            }}
            disabled={!lastPageLoaded}
            children={props.children}
            events={{
              onFeedbackOpen,
              onFavoriteOpen,
              onFavoriteClick,
              onPage: changePage,
              onSearch: setSearchTerm,
              onPageScale: setPageScale,
              onDownload: handleDownload,
              onPageNumber: setPageNumber,
              onThumbnail: setShowThumbnails,
              onSearchResults: setSearchResults,
              onHighlightedSearchResult: setHighlightedResult,
            }}
          />
        )}
        <Loading
          showThumbnails={showThumbnails}
          width={`${_dimensions[thumbnailWidth]}rem`}
          classes={classes}
          hidden={!isLoading}
        />
        <Grid
          container
          component={Document}
          ref={ref}
          file={url}
          options={options}
          noData={null}
          error={errorComponent}
          onItemClick={onItemClick}
          className={isLoading ? '' : classes.document}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={() => { setError(true) }}
          onSourceError={() => { setError(true) }}
          loading={(
            <Loading
              showThumbnails={showThumbnails}
              width={`${_dimensions[thumbnailWidth]}rem`}
              classes={classes}
              hidden={isLoading}
            />
          )}
          flexWrap={'nowrap'}
        >
          <Backdrop
            open={!lastPageLoaded}
            className={classes.loader}
          >
            <CircularProgress
              color={'inherit'}
              size={'1.5rem'}
            /> {loadingLabel}
          </Backdrop>
          {showThumbnails && (
            <Thumbnails
              width={`${_dimensions[thumbnailWidth]}rem`}
              pages={numPages}
              currentPage={pageNumber}
              currentPageLoaded={currentPageLoaded}
            />
          )}
          <Grid
            item
            xs={true}
            className={classes.pageContainer}
          >
            <Pages
              pages={numPages}
              pageScale={pageScale}
              searchTerm={searchTerm}
              width={documentWidth}
              currentPage={pageNumber || initialPage}
              showThumbnails={showThumbnails}
              currentPageLoaded={currentPageLoaded}
              lastPageLoaded={lastPageLoaded}
              events={{
                onSearch: setSearchResults,
                onPageScale: setPageScale,
                onCurrentPageLoaded: setCurrentPageLoaded,
                onLastPageLoaded: setLastPageLoaded,
              }}
              highlightedSearchResult={highlightedResult}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default SimplePdf
